<template>
	<div class="report-section communication-section">
		<section-header :number="'02'" title="通讯分析" />
		<div class="section-content">
			<div class="stats-grid" pdf-truncation-check-point>
				<stat-card
					label="本期日均通讯率"
					:value="data.communicationRate"
					unit="%"
					:icon="riJunTongXunLv"
					backgroundColor="#edf8fe"
				/>
				<stat-card
					v-if="type !== 3"
					label="同比"
					:value="data.communicationRateYoy"
					unit="%"
					:trend="data.communicationRateYoy"
					:icon="tongBi"
					backgroundColor="#edf0ff"
				/>
				<stat-card
					label="环比"
					:value="data.communicationRateMom"
					unit="%"
					:trend="data.communicationRateMom"
					:icon="huanBi"
					backgroundColor="#ece4f7"
				/>
			</div>
			<div class="charts-row">
				<Panel title="设备类型通讯率统计Top5" class="chart-container" pdf-truncation-check-point>
					<horizontal-bar-chart
						:chart-options="communicationSuccessRateOptions"
						height="190px"
						chart-style="blue"
					/>
				</Panel>
				<Panel title="设备类型通讯率统计Bottom5" class="chart-container" pdf-truncation-check-point>
					<horizontal-bar-chart :chart-options="communicationTypeDistributionOptions" height="190px" />
				</Panel>
			</div>

			<Panel
				v-if="type === 1"
				title="本期通讯率趋势"
				class="chart-container full-width"
				pdf-truncation-check-point
			>
				<line-chart :chart-options="monthlyCommunicationTrendOptions" height="190px" chart-style="blue" />
			</Panel>

			<Panel v-else title="每月同比日均通讯率" class="chart-container full-width" pdf-truncation-check-point>
				<bar-chart :chart-options="monthlyCommunicationTrendOptions" height="190px" chart-style="purple" />
			</Panel>
		</div>
	</div>
</template>

<script>
import SectionHeader from '../components/SectionHeader.vue'
import StatCard from '../components/StatCard.vue'
import huanBi from '../icons/huanbi.png'
import tongBi from '../icons/tongbi.png'
import riJunTongXunLv from '../icons/rijuntongxunlv.png'
import Panel from '../components/panel.vue'
import HorizontalBarChart from '../components/horizontalBarChart.vue'
import LineChart from '../components/lineChart.vue'
import BarChart from '../components/barChart.vue'
import { fillMissingData, shouldRotate } from '../components/chartMixin'

export default {
	name: 'CommunicationSection',
	components: {
		SectionHeader,
		StatCard,
		Panel,
		HorizontalBarChart,
		LineChart,
		BarChart,
	},
	props: {
		data: {
			type: Object,
			default: () => {},
		},
		type: {
			type: Number,
			default: 1,
		},
	},
	data() {
		return {
			riJunTongXunLv,
			tongBi,
			huanBi,
		}
	},
	computed: {
		deviceTypeRateData() {
			return this.data.deviceTypeCommunicationRate || []
		},
		communicationSuccessRateOptions() {
			const data = this.deviceTypeRateData.slice(0, 5).sort((a, b) => a.count - b.count)
			return {
				yAxis: {
					data: data.map(item => item.type),
				},
				series: [
					{
						data: data.map(item => item.count),
					},
				],
			}
		},

		// 通信类型分布柱状图配置
		communicationTypeDistributionOptions() {
			const data = this.deviceTypeRateData.slice(-5).sort((a, b) => b.count - a.count)
			return {
				yAxis: {
					data: data.map(item => item.type),
				},
				series: [
					{
						data: data.map(item => item.count),
					},
				],
			}
		},

		// 月度通信趋势折线图配置
		monthlyCommunicationTrendOptions() {
			let data = this.data.trendCommunicationRate || []
			const type = this.type
			data = data.map(item => {
				if (type === 1) {
					item.name = item.type.split('-').slice(1).join('.')
				} else {
					item.name = item.type.split('-').pop() + '月'
				}
				return item
			})

			if (data.length) {
				const { year, dateSub } = this.data
				const fillList = []
				if (type === 1) {
					const month = dateSub < 10 ? '0' + dateSub : dateSub
					const endDate =
						this.dayjs(`${year}-${month}-01`).endOf('month').format('YYYY-MM-DD').split('-').pop() - 0
					for (let i = 1; i <= endDate; i++) {
						fillList.push(`${month}.${i < 10 ? '0' + i : i}`)
					}
				} else if (type === 2) {
					for (let s = (dateSub - 1) * 3 + 1; s <= (dateSub - 1) * 3 + 3; s++) {
						fillList.push(`${s < 10 ? '0' + s : s}月`)
					}
				} else if (type === 3) {
					for (let s = 1; s <= 12; s++) {
						fillList.push(`${s < 10 ? '0' + s : s}月`)
					}
				}
				data = fillMissingData(data, fillList, ['count', 'yoyCount'])
			}

			return {
				grid: {
					top: shouldRotate(type === 1 ? [{ data }] : [{ data }, { data }])
						? type === 1
							? '18%'
							: '24%'
						: '16%',
				},
				yAxis: {
					name: '%',
					nameGap: shouldRotate(type === 1 ? [{ data }] : [{ data }, { data }]) ? 20 : 12,
					nameTextStyle: {
						color: '#999',
					},
					max: function (value) {
						const max = (value.max * 1.2) | 0
						if (max < 15) {
							return Math.ceil(max / 2) * 2
						}
						if (max < 30) {
							return Math.ceil(max / 5) * 5
						}
						if (max < 100) {
							return Math.ceil(max / 20) * 20
						}
						return 100
					},
				},
				xAxis: {
					data: data.map(item => item.name),
				},
				series:
					type === 1
						? [
								{
									data: data.map(item => item.count),
								},
						  ]
						: [
								{
									name: '去年',
									data: data.map(item => item.yoyCount),
									barWidth: type === 2 ? 25 : 15,
								},
								{
									name: '今年',
									data: data.map(item => item.count),
									barWidth: type === 2 ? 25 : 15,
								},
						  ],
			}
		},
	},
}
</script>

<style scoped lang="scss">
.device-status-columns {
	width: 100%;
}
</style>
